import React, { useCallback, useEffect, useState } from 'react'
import {
  Archive,
  CheckCircle2,
  FilePlus2,
  LoaderCircle,
  Pencil,
  Save,
  Send,
  Trash2,
} from 'lucide-react'
import { api, getApiErrorMessage } from '../../lib/api'

const EMPTY_FORM = {
  id: null,
  scenarioCode: '',
  title: '',
  domainLabel: '',
  insuranceTypeLabel: '',
  consultationStageLabel: '',
  displayOrder: 0,
  goal: '',
  openingLines: '',
  hiddenFacts: '',
  surpriseQuestions: '',
  requiredDiscovery: '',
  requiredExplanation: '',
  safeNextAction: '',
  status: 'DRAFT',
  statusLabel: '작성 중',
  hasDraft: false,
  activeVersionNo: null,
  draftVersionNo: null,
}

function unwrap(response) {
  return response?.data?.data ?? response?.data ?? null
}

function linesToText(value) {
  return Array.isArray(value) ? value.join('\n') : ''
}

function textToLines(value) {
  return String(value || '')
    .split(/\r?\n/)
    .map((item) => item.trim())
    .filter(Boolean)
}

function toForm(item) {
  if (!item) return { ...EMPTY_FORM }
  return {
    ...EMPTY_FORM,
    ...item,
    openingLines: linesToText(item.openingLines),
    hiddenFacts: linesToText(item.hiddenFacts),
    surpriseQuestions: linesToText(item.surpriseQuestions),
    requiredDiscovery: linesToText(item.requiredDiscovery),
    requiredExplanation: linesToText(item.requiredExplanation),
  }
}

function payload(form) {
  return {
    scenarioCode: form.scenarioCode.trim(),
    title: form.title.trim(),
    domainLabel: form.domainLabel.trim(),
    insuranceTypeLabel: form.insuranceTypeLabel.trim(),
    consultationStageLabel: form.consultationStageLabel.trim(),
    displayOrder: Number(form.displayOrder) || 0,
    goal: form.goal.trim(),
    openingLines: textToLines(form.openingLines),
    hiddenFacts: textToLines(form.hiddenFacts),
    surpriseQuestions: textToLines(form.surpriseQuestions),
    requiredDiscovery: textToLines(form.requiredDiscovery),
    requiredExplanation: textToLines(form.requiredExplanation),
    safeNextAction: form.safeNextAction.trim(),
  }
}

function Field({ label, children, className = '' }) {
  return (
    <label className={`block min-w-0 ${className}`}>
      <span className="text-sm font-medium text-slate-700">{label}</span>
      {children}
    </label>
  )
}

function StatusBadge({ form }) {
  const className = form.status === 'ACTIVE'
    ? 'bg-emerald-50 text-emerald-700 ring-emerald-200'
    : form.status === 'ARCHIVED'
      ? 'bg-slate-100 text-slate-600 ring-slate-200'
      : 'bg-amber-50 text-amber-800 ring-amber-200'
  return (
    <span className={`inline-flex rounded-md px-2 py-1 text-xs font-semibold ring-1 ring-inset ${className}`}>
      {form.statusLabel}
    </span>
  )
}

export default function CounselPracticeScenarioManager({ onCatalogChanged }) {
  const [items, setItems] = useState([])
  const [form, setForm] = useState({ ...EMPTY_FORM })
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')

  const load = useCallback(async (preferredId = null) => {
    setLoading(true)
    setError('')
    try {
      const response = await api.get('/counsel-practice/admin/scenarios', { __silent: true })
      const data = unwrap(response)
      const nextItems = Array.isArray(data?.items) ? data.items : []
      setItems(nextItems)
      const next = nextItems.find((item) => item.id === preferredId)
        || nextItems[0]
      if (next) setForm(toForm(next))
    } catch (e) {
      setError(getApiErrorMessage(e, '시나리오 목록을 불러오지 못했습니다.'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const update = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }))
    setNotice('')
  }

  const startNew = () => {
    setForm({ ...EMPTY_FORM, displayOrder: (items.length + 1) * 10 })
    setError('')
    setNotice('')
  }

  const selectItem = (item) => {
    if (busy) return
    setForm(toForm(item))
    setError('')
    setNotice('')
  }

  const save = async () => {
    if (busy) return
    setBusy(true)
    setError('')
    setNotice('')
    try {
      const response = form.id
        ? await api.put(`/counsel-practice/admin/scenarios/${form.id}/draft`, payload(form), { __silent: true })
        : await api.post('/counsel-practice/admin/scenarios', payload(form), { __silent: true })
      const saved = unwrap(response)
      await load(saved?.id)
      setNotice('초안을 저장했습니다.')
    } catch (e) {
      setError(getApiErrorMessage(e, '시나리오 초안을 저장하지 못했습니다.'))
    } finally {
      setBusy(false)
    }
  }

  const publish = async () => {
    if (!form.id || !form.hasDraft || busy) return
    if (!window.confirm('저장한 초안을 연습 목록에 게시할까요?')) return
    setBusy(true)
    setError('')
    setNotice('')
    try {
      const response = await api.post(`/counsel-practice/admin/scenarios/${form.id}/publish`, {}, { __silent: true })
      const published = unwrap(response)
      await load(published?.id)
      await onCatalogChanged?.()
      setNotice('시나리오를 게시했습니다.')
    } catch (e) {
      setError(getApiErrorMessage(e, '시나리오를 게시하지 못했습니다.'))
    } finally {
      setBusy(false)
    }
  }

  const toggleAvailability = async () => {
    if (!form.id || busy) return
    const active = form.status !== 'ACTIVE'
    const action = active ? '다시 사용' : '사용 중지'
    if (!window.confirm(`이 시나리오를 ${action}할까요?`)) return
    setBusy(true)
    setError('')
    setNotice('')
    try {
      const response = await api.put(
        `/counsel-practice/admin/scenarios/${form.id}/availability`,
        { active },
        { __silent: true },
      )
      const updated = unwrap(response)
      await load(updated?.id)
      await onCatalogChanged?.()
      setNotice(active ? '시나리오를 다시 사용할 수 있습니다.' : '연습 목록에서 시나리오를 내렸습니다.')
    } catch (e) {
      setError(getApiErrorMessage(e, `${action}하지 못했습니다.`))
    } finally {
      setBusy(false)
    }
  }

  const deleteDraft = async () => {
    if (!form.id || form.activeVersionNo || busy) return
    if (!window.confirm('아직 게시하지 않은 이 시나리오 초안을 삭제할까요?')) return
    setBusy(true)
    setError('')
    setNotice('')
    try {
      await api.delete(`/counsel-practice/admin/scenarios/${form.id}/draft`, { __silent: true })
      await load()
      setNotice('시나리오 초안을 삭제했습니다.')
    } catch (e) {
      setError(getApiErrorMessage(e, '시나리오 초안을 삭제하지 못했습니다.'))
    } finally {
      setBusy(false)
    }
  }

  if (loading) {
    return (
      <div className="flex min-h-[28rem] items-center justify-center text-slate-500">
        <LoaderCircle className="h-6 w-6 animate-spin" aria-label="시나리오 불러오는 중" />
      </div>
    )
  }

  return (
    <section className="mt-5" aria-labelledby="scenario-manager-title" data-testid="counsel-scenario-manager">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 pb-4">
        <div>
          <h2 id="scenario-manager-title" className="text-lg font-semibold text-slate-950">시나리오 관리</h2>
          <p className="mt-1 text-sm text-slate-600">초안을 게시해야 연습 목록에 반영됩니다.</p>
        </div>
        <button
          type="button"
          onClick={startNew}
          disabled={busy}
          className="inline-flex h-9 items-center gap-2 rounded-md border border-slate-300 bg-white px-3 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
        >
          <FilePlus2 className="h-4 w-4" aria-hidden="true" />
          새 시나리오
        </button>
      </div>

      {error && (
        <div className="mt-4 rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700" role="alert">
          {error}
        </div>
      )}
      {notice && (
        <div className="mt-4 flex items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700" role="status">
          <CheckCircle2 className="h-4 w-4 shrink-0" aria-hidden="true" />
          {notice}
        </div>
      )}

      <div className="mt-5 grid min-h-[680px] border border-slate-200 bg-white lg:grid-cols-[18rem_minmax(0,1fr)]">
        <aside className="border-b border-slate-200 lg:border-b-0 lg:border-r" aria-label="시나리오 목록">
          <div className="max-h-[680px] overflow-y-auto">
            {items.length === 0 && <p className="px-4 py-8 text-center text-sm text-slate-500">등록된 시나리오가 없습니다.</p>}
            {items.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => selectItem(item)}
                disabled={busy}
                className={`w-full border-b border-slate-100 px-4 py-3 text-left hover:bg-slate-50 disabled:opacity-50 ${
                  form.id === item.id ? 'bg-sky-50' : 'bg-white'
                }`}
              >
                <div className="line-clamp-2 text-sm font-semibold leading-5 text-slate-900">{item.title}</div>
                <div className="mt-1 truncate text-xs text-slate-500">{item.insuranceTypeLabel}</div>
                <div className="mt-2 flex items-center justify-between gap-2 text-xs">
                  <span className="truncate text-slate-500">{item.scenarioCode}</span>
                  <span className={item.hasDraft ? 'font-semibold text-amber-700' : 'text-slate-500'}>
                    {item.hasDraft ? '초안 있음' : item.statusLabel}
                  </span>
                </div>
              </button>
            ))}
          </div>
        </aside>

        <form
          className="min-w-0 p-4 sm:p-6"
          onSubmit={(event) => {
            event.preventDefault()
            save()
          }}
        >
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 pb-4">
            <div className="flex min-w-0 items-center gap-3">
              <Pencil className="h-5 w-5 shrink-0 text-sky-700" aria-hidden="true" />
              <div className="min-w-0">
                <div className="truncate font-semibold text-slate-950">{form.id ? form.title || '시나리오 수정' : '새 시나리오'}</div>
                {form.id && (
                  <div className="mt-1 text-xs text-slate-500">
                    게시본 {form.activeVersionNo ? `${form.activeVersionNo}판` : '없음'}
                    {form.draftVersionNo ? ` · 초안 ${form.draftVersionNo}판` : ''}
                  </div>
                )}
              </div>
            </div>
            {form.id && <StatusBadge form={form} />}
          </div>

          <div className="mt-5 grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            <Field label="시나리오 코드">
              <input
                value={form.scenarioCode}
                onChange={(event) => update('scenarioCode', event.target.value.toUpperCase())}
                disabled={Boolean(form.id)}
                maxLength={40}
                className="mt-2 h-10 w-full rounded-md border border-slate-300 px-3 text-sm disabled:bg-slate-100"
                required
              />
            </Field>
            <Field label="시나리오 제목" className="xl:col-span-2">
              <input
                value={form.title}
                onChange={(event) => update('title', event.target.value)}
                maxLength={120}
                className="mt-2 h-10 w-full rounded-md border border-slate-300 px-3 text-sm"
                required
              />
            </Field>
            <Field label="상담 분야">
              <input value={form.domainLabel} onChange={(event) => update('domainLabel', event.target.value)} maxLength={60} className="mt-2 h-10 w-full rounded-md border border-slate-300 px-3 text-sm" required />
            </Field>
            <Field label="보험 종류">
              <input value={form.insuranceTypeLabel} onChange={(event) => update('insuranceTypeLabel', event.target.value)} maxLength={60} className="mt-2 h-10 w-full rounded-md border border-slate-300 px-3 text-sm" required />
            </Field>
            <Field label="상담 단계">
              <input value={form.consultationStageLabel} onChange={(event) => update('consultationStageLabel', event.target.value)} maxLength={60} className="mt-2 h-10 w-full rounded-md border border-slate-300 px-3 text-sm" required />
            </Field>
            <Field label="표시 순서">
              <input type="number" min="0" max="100000" value={form.displayOrder} onChange={(event) => update('displayOrder', event.target.value)} className="mt-2 h-10 w-full rounded-md border border-slate-300 px-3 text-sm" />
            </Field>
          </div>

          <div className="mt-5 grid gap-5 xl:grid-cols-2">
            <Field label="연습 목표" className="xl:col-span-2">
              <textarea value={form.goal} onChange={(event) => update('goal', event.target.value)} rows={2} maxLength={1000} className="mt-2 w-full resize-y rounded-md border border-slate-300 px-3 py-2 text-sm leading-6" required />
            </Field>
            <Field label="고객의 첫 질문 · 한 줄에 하나">
              <textarea value={form.openingLines} onChange={(event) => update('openingLines', event.target.value)} rows={4} className="mt-2 w-full resize-y rounded-md border border-slate-300 px-3 py-2 text-sm leading-6" required />
            </Field>
            <Field label="고객의 숨은 정보 · 한 줄에 하나">
              <textarea value={form.hiddenFacts} onChange={(event) => update('hiddenFacts', event.target.value)} rows={4} className="mt-2 w-full resize-y rounded-md border border-slate-300 px-3 py-2 text-sm leading-6" />
            </Field>
            <Field label="예상 밖 질문 · 한 줄에 하나">
              <textarea value={form.surpriseQuestions} onChange={(event) => update('surpriseQuestions', event.target.value)} rows={4} className="mt-2 w-full resize-y rounded-md border border-slate-300 px-3 py-2 text-sm leading-6" />
            </Field>
            <Field label="반드시 확인할 내용 · 한 줄에 하나">
              <textarea value={form.requiredDiscovery} onChange={(event) => update('requiredDiscovery', event.target.value)} rows={4} className="mt-2 w-full resize-y rounded-md border border-slate-300 px-3 py-2 text-sm leading-6" required />
            </Field>
            <Field label="반드시 설명할 내용 · 한 줄에 하나">
              <textarea value={form.requiredExplanation} onChange={(event) => update('requiredExplanation', event.target.value)} rows={4} className="mt-2 w-full resize-y rounded-md border border-slate-300 px-3 py-2 text-sm leading-6" required />
            </Field>
            <Field label="안전한 다음 단계">
              <textarea value={form.safeNextAction} onChange={(event) => update('safeNextAction', event.target.value)} rows={4} maxLength={1000} className="mt-2 w-full resize-y rounded-md border border-slate-300 px-3 py-2 text-sm leading-6" required />
            </Field>
          </div>

          <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 pt-5">
            <div>
              {form.id && !form.activeVersionNo ? (
                <button
                  type="button"
                  onClick={deleteDraft}
                  disabled={busy}
                  className="inline-flex h-10 items-center gap-2 rounded-md border border-rose-300 bg-white px-3 text-sm font-semibold text-rose-700 hover:bg-rose-50 disabled:opacity-40"
                >
                  <Trash2 className="h-4 w-4" aria-hidden="true" />
                  초안 삭제
                </button>
              ) : form.id ? (
                <button
                  type="button"
                  onClick={toggleAvailability}
                  disabled={busy || (form.status !== 'ACTIVE' && !form.activeVersionNo)}
                  className="inline-flex h-10 items-center gap-2 rounded-md border border-slate-300 bg-white px-3 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-40"
                >
                  <Archive className="h-4 w-4" aria-hidden="true" />
                  {form.status === 'ACTIVE' ? '사용 중지' : '다시 사용'}
                </button>
              ) : null}
            </div>
            <div className="flex flex-wrap gap-2">
              {form.id && (
                <button
                  type="button"
                  onClick={publish}
                  disabled={busy || !form.hasDraft}
                  className="inline-flex h-10 items-center gap-2 rounded-md border border-sky-300 bg-sky-50 px-3 text-sm font-semibold text-sky-700 hover:bg-sky-100 disabled:opacity-40"
                >
                  <Send className="h-4 w-4" aria-hidden="true" />
                  초안 게시
                </button>
              )}
              <button
                type="submit"
                disabled={busy}
                className="inline-flex h-10 items-center gap-2 rounded-md bg-slate-900 px-4 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50"
                data-testid="counsel-scenario-save"
              >
                {busy ? <LoaderCircle className="h-4 w-4 animate-spin" aria-hidden="true" /> : <Save className="h-4 w-4" aria-hidden="true" />}
                초안 저장
              </button>
            </div>
          </div>
        </form>
      </div>
    </section>
  )
}
